import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import type { MenuProps } from "antd";
import { Menu, Typography, Dropdown, Space } from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
  CloudOutlined,
  HomeOutlined,
  UserOutlined,
  DashboardOutlined,
  KeyOutlined,
  WifiOutlined,
  ControlOutlined,
  AppstoreOutlined,
  CompassOutlined,
  RadarChartOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

import type { GetTenantResponse, ListTenantsResponse } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";
import { ListTenantsRequest } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import type { GetVersionResponse } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";

import type { OptionCallbackFunc, OptionsCallbackFunc } from "../components/Autocomplete";
import Autocomplete from "../components/Autocomplete";
import Admin from "../components/Admin";
import ThemeToggle from "../components/ThemeToggle";
import TenantStore from "../stores/TenantStore";
import SessionStore from "../stores/SessionStore";
import InternalStore from "../stores/InternalStore";

function SideMenu({ isMobile = false, onMobileMenuClose }: { isMobile?: boolean; onMobileMenuClose?: () => void }) {
  const [tenantId, setTenantId] = useState<string>("");
  const [tenantName, setTenantName] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [version, setVersion] = useState<string>("");
  const [openKeys, setOpenKeys] = useState<string[]>(["tenant"]);
  const [allTenants, setAllTenants] = useState<Array<{id: string, name: string}>>([]);

  const location = useLocation();
  const navigate = useNavigate();

  const setTenant = () => {
    const currentTenantId = SessionStore.getTenantId();
    setTenantId(currentTenantId);
    
    if (currentTenantId) {
      TenantStore.get(currentTenantId, (resp: GetTenantResponse) => {
        const tenant = resp.getTenant();
        if (tenant) {
          setTenantName(tenant.getName());
        }
      });
    } else {
      setTenantName("");
    }
  };

  const getTenantOptions = (search: string, fn: OptionsCallbackFunc) => {
    const req = new ListTenantsRequest();
    req.setSearch(search);
    req.setLimit(10);

    TenantStore.list(req, (resp: ListTenantsResponse) => {
      const options = resp.getResultList().map((o, i) => {
        return { label: o.getName(), value: o.getId() };
      });
      fn(options);
    });
  };

  const loadAllTenants = () => {
    const req = new ListTenantsRequest();
    req.setLimit(999); // Get all tenants

    TenantStore.list(req, (resp: ListTenantsResponse) => {
      const tenants = resp.getResultList().map((tenant) => ({
        id: tenant.getId(),
        name: tenant.getName()
      }));
      setAllTenants(tenants);
    });
  };

  const getTenantOption = (id: string, fn: OptionCallbackFunc) => {
    TenantStore.get(id, (resp: GetTenantResponse) => {
      const tenant = resp.getTenant();
      if (tenant) {
        fn({ label: tenant.getName(), value: tenant.getId() });
      }
    });
  };

  const onTenantSelect = (value: string) => {
    SessionStore.setTenantId(value);
    navigate(`/tenants/${value}`);
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleMenuClick = () => {
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const handleOpenChange = (keys: string[]) => {
    // Always keep tenant section open, add it if not present
    const updatedKeys = keys.includes('tenant') ? keys : [...keys, 'tenant'];
    setOpenKeys(updatedKeys);
  };

  const parseLocation = useCallback(() => {
    const path = location.pathname;
    const tenantRe = /\/tenants\/([\w-]{36})/g;
    const match = tenantRe.exec(path);

    if (match !== null && tenantId !== match[1]) {
      SessionStore.setTenantId(match[1]);
    }

    // devstat dashboard
    if (path === "/devstat-dashboard") {
      setSelectedKey("devstat-dashboard");
    }

    // devstat details
    if (path === "/mqtt-devices" || path.startsWith("/mqtt-devices/")) {
      setSelectedKey("mqtt-devices");
    }

    // ns dashboard
    if (path === "/dashboard") {
      setSelectedKey("ns-dashboard");
    }

    // ns tenants
    if (/\/tenants(\/([\w-]{36}\/edit|create))?/g.exec(path)) {
      setSelectedKey("ns-tenants");
    }

    // ns tenants
    if (/\/users(\/([\w-]{36}\/edit|create))?/g.exec(path)) {
      setSelectedKey("ns-users");
    }

    // ns api keys
    if (/\/api-keys(\/([\w-]{36}\/edit|create))?/g.exec(path)) {
      setSelectedKey("ns-api-keys");
    }

    // ns device-profile templates
    if (/\/device-profile-templates(\/([\w-]{36}\/edit|create))?/g.exec(path)) {
      setSelectedKey("ns-device-profile-templates");
    }

    if (/\/regions.*/g.exec(path)) {
      setSelectedKey("ns-regions");
    }

    // tenant dashboard
    if (/\/tenants\/[\w-]{36}/g.exec(path)) {
      setSelectedKey("tenant-dashboard");
    }

    // tenant users
    if (/\/tenants\/[\w-]{36}\/users.*/g.exec(path)) {
      setSelectedKey("tenant-users");
    }

    // tenant api-keys
    if (/\/tenants\/[\w-]{36}\/api-keys.*/g.exec(path)) {
      setSelectedKey("tenant-api-keys");
    }

    // tenant device-profiles
    if (/\/tenants\/[\w-]{36}\/device-profiles.*/g.exec(path)) {
      setSelectedKey("tenant-device-profiles");
    }

    // tenant gateways
    if (/\/tenants\/[\w-]{36}\/gateways.*/g.exec(path)) {
      setSelectedKey("tenant-gateways");
    }

    // tenant gateway-mesh
    if (/\/tenants\/[\w-]{36}\/gateways\/mesh.*/g.exec(path)) {
      setSelectedKey("tenant-gateways-mesh");
    }

    // tenant applications
    if (/\/tenants\/[\w-]{36}\/applications.*/g.exec(path)) {
      setSelectedKey("tenant-applications");
    }
  }, [location.pathname, tenantId]);

  useEffect(() => {
    SessionStore.on("tenant.change", setTenant);
    setTenant();
    parseLocation();
    loadAllTenants(); // Load all tenants on mount

    if (SessionStore.isAdmin()) {
      InternalStore.getVersion((resp: GetVersionResponse) => {
        setVersion(resp.getVersion());
      });
    }

    return () => {
      SessionStore.removeListener("tenant.change", setTenant);
    };
  }, [parseLocation]);

  useEffect(() => {
    parseLocation();
  }, [location, parseLocation]);

  const items: MenuProps["items"] = [];

  // DevStat Dashboard section (added above tenant section)
  items.push({
    key: "devstat-dashboard",
    icon: <BarChartOutlined />,
    label: <Link to="/devstat-dashboard" onClick={handleMenuClick}>DevStat Dashboard</Link>,
  });

  // DevStat Details section (added below DevStat Dashboard)
  items.push({
    key: "mqtt-devices",
    icon: <WifiOutlined />,
    label: <Link to="/mqtt-devices" onClick={handleMenuClick}>DevStat Details</Link>,
  });

  // Create tenant dropdown menu
  const tenantDropdownMenu = (
    <div style={{ padding: '4px 0', minWidth: 200, maxHeight: 300, overflowY: 'auto' }}>
      {allTenants.map((tenant) => (
        <div
          key={tenant.id}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            borderLeft: tenant.id === tenantId ? '3px solid #1890ff' : '3px solid transparent',
            transition: 'border-left 0.2s'
          }}
          onClick={() => {
            onTenantSelect(tenant.id);
            if (isMobile && onMobileMenuClose) {
              onMobileMenuClose();
            }
          }}
        >
          <div style={{ 
            fontWeight: tenant.id === tenantId ? 500 : 400,
            color: 'inherit'
          }}>
            {tenant.name}
          </div>
        </div>
      ))}
    </div>
  );

  // Tenant section (moved above NS Admin) - always expanded
  if (tenantId !== "") {
    items.push({
      key: "tenant",
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <HomeOutlined />
            <span>{tenantName || 'Tenant'}</span>
          </Space>
          <Dropdown 
            overlay={tenantDropdownMenu} 
            placement="bottomLeft" 
            trigger={['click']}
            overlayStyle={{ minWidth: 200 }}
            destroyPopupOnHide={true}
            onOpenChange={(open) => {
              if (open) {
                loadAllTenants(); // Reload tenants when dropdown opens
              }
            }}
          >
            <DownOutlined 
              style={{ fontSize: '12px', cursor: 'pointer' }} 
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>
      ),
      children: [
        {
          key: "tenant-dashboard",
          icon: <DashboardOutlined />,
          label: <Link to={`/tenants/${tenantId}`} onClick={handleMenuClick}>Dashboard</Link>,
        },
        {
          key: "tenant-gateways",
          icon: <WifiOutlined />,
          label: <Link to={`/tenants/${tenantId}/gateways`} onClick={handleMenuClick}>Gateways</Link>,
        },
        {
          key: "tenant-applications",
          icon: <AppstoreOutlined />,
          label: <Link to={`/tenants/${tenantId}/applications`} onClick={handleMenuClick}>Applications</Link>,
        },
        {
          key: "tenant-advanced",
          label: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Space>
                <ControlOutlined />
                <span>Advanced Options</span>
              </Space>
              <DownOutlined style={{ fontSize: '12px' }} />
            </div>
          ),
          type: "submenu",
          children: [
            {
              key: "tenant-users",
              icon: <UserOutlined />,
              label: <Link to={`/tenants/${tenantId}/users`} onClick={handleMenuClick}>Users</Link>,
            },
            {
              key: "tenant-api-keys",
              icon: <KeyOutlined />,
              label: <Link to={`/tenants/${tenantId}/api-keys`} onClick={handleMenuClick}>API Keys</Link>,
            },
            {
              key: "tenant-device-profiles",
              icon: <ControlOutlined />,
              label: <Link to={`/tenants/${tenantId}/device-profiles`} onClick={handleMenuClick}>Device Profiles</Link>,
            },
            {
              key: "tenant-gateways-mesh",
              icon: <RadarChartOutlined />,
              label: <Link to={`/tenants/${tenantId}/gateways/mesh/relays`} onClick={handleMenuClick}>Gateway Mesh</Link>,
            },
          ],
        },
      ],
    });
  }

  // NS Admin section (renamed from Network Server)
  if (SessionStore.isAdmin()) {
    items.push({
      key: "ns",
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <CloudOutlined />
            <span>NS Admin</span>
          </Space>
          <DownOutlined style={{ fontSize: '12px' }} />
        </div>
      ),
      type: "submenu",
      children: [
        {
          key: "ns-dashboard",
          icon: <DashboardOutlined />,
          label: <Link to="/dashboard" onClick={handleMenuClick}>Dashboard</Link>,
        },
        {
          key: "ns-tenants",
          icon: <HomeOutlined />,
          label: <Link to="/tenants" onClick={handleMenuClick}>Tenants</Link>,
        },
        {
          key: "ns-users",
          icon: <UserOutlined />,
          label: <Link to="/users" onClick={handleMenuClick}>Users</Link>,
        },
        {
          key: "ns-api-keys",
          icon: <KeyOutlined />,
          label: <Link to="/api-keys" onClick={handleMenuClick}>API Keys</Link>,
        },
        {
          key: "ns-device-profile-templates",
          icon: <ControlOutlined />,
          label: <Link to="/device-profile-templates" onClick={handleMenuClick}>Device Profile Templates</Link>,
        },
        {
          key: "ns-regions",
          icon: <CompassOutlined />,
          label: <Link to="/regions" onClick={handleMenuClick}>Regions</Link>,
        },
      ],
    });
  } else {
    items.push({
      key: "ns",
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <CloudOutlined />
            <span>NS Admin</span>
          </Space>
          <DownOutlined style={{ fontSize: '12px' }} />
        </div>
      ),
      type: "submenu",
      children: [
        {
          key: "ns-regions",
          icon: <CompassOutlined />,
          label: <Link to="/regions" onClick={handleMenuClick}>Regions</Link>,
        },
      ],
    });
  }

  return (
    <div className={isMobile ? "mobile-menu-content" : ""}>
      <Menu
        mode="inline"
        openKeys={openKeys}
        selectedKeys={[selectedKey]}
        onOpenChange={handleOpenChange}
        expandIcon={() => {
          return <div></div>;
        }}
        items={items}
      />
      <Admin>
        <Typography.Text type="secondary" className="version">
          Version: v{version}
        </Typography.Text>
      </Admin>
      
      {/* Theme Toggle at the bottom */}
      <div className="theme-toggle-container">
        <ThemeToggle size="middle" />
      </div>
    </div>
  );
}

export default SideMenu;
